import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { PencilIcon, TypeIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { PROJECT_STATUS_CONFIG } from "@/features/projects/config";
import { UpdateProjectSchema, updateProjectSchema } from "@/features/projects/schemas";
import { updateProject } from "@/features/projects/services/project";
import { Project, ProjectStatus } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import Success from "@/shared/components/Success";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface UpdateProjectFormProps {
  project: Project;
  disabled?: boolean;
}

export default function UpdateProjectForm({ project, disabled = false }: UpdateProjectFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<UpdateProjectSchema>({
    resolver: zodResolver(updateProjectSchema),
    defaultValues: {
      id: project.id,
      name: project.name,
      description: project.description,
      status: project.status,
    },
  });

  const watchedStatus = form.watch("status");
  const selectedStatus = PROJECT_STATUS_CONFIG[watchedStatus];

  const mutation = useMutation<string, AppError | Error, UpdateProjectSchema>({
    mutationFn: updateProject,
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PROJECTS], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, id], exact: true });
    },
  });

  async function onSubmit(values: UpdateProjectSchema) {
    mutation.mutate(values);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <TypeIcon className="size-4 text-primary" />
          <CardTitle>Project identity</CardTitle>
        </div>
        <CardDescription>The name, description and status are visible to all project members.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {mutation.isError && <Error title="Unable to update project" error={mutation.error} />}
            {mutation.isSuccess && <Success title="Project updated" description="Your changes have been saved successfully." />}
            <FieldGroup>
              <Controller
                name="id"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <Input type="hidden" {...field} disabled={mutation.isPending || disabled} aria-invalid={fieldState.invalid} />
                  </Field>
                )}
              />
              <Controller
                name="name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Project name</FieldLabel>
                    <Input {...field} disabled={mutation.isPending || disabled} aria-invalid={fieldState.invalid} placeholder="e.g. Customer Portal" autoComplete="off" />
                    <FieldDescription>Keep it short and descriptive. Maximum 64 characters.</FieldDescription>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="description"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Description</FieldLabel>
                    <Textarea {...field} disabled={mutation.isPending || disabled} aria-invalid={fieldState.invalid} placeholder="Describe what this project is for…" autoComplete="off" />
                    <FieldDescription>Helps new members understand the scope of the project at a glance.</FieldDescription>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="status"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="project-status">Status</FieldLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={mutation.isPending || disabled}>
                      <SelectTrigger id="project-status" className="w-full">
                        <SelectValue>
                          <div className="flex items-center gap-2">
                            <selectedStatus.icon className={`size-3.5 shrink-0 ${selectedStatus.className}`} />
                            <span>{field.value}</span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent position="popper" className="w-(--radix-select-trigger-width)">
                        {(Object.entries(PROJECT_STATUS_CONFIG) as [ProjectStatus, (typeof PROJECT_STATUS_CONFIG)[ProjectStatus]][]).map(([value, cfg]) => (
                          <SelectItem key={value} value={value} className="cursor-pointer">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-2">
                                <cfg.icon className={`size-3.5 shrink-0 ${cfg.className}`} />
                                <span className="font-medium">{value}</span>
                              </div>
                              <span className="text-xs text-muted-foreground pl-5">{cfg.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldDescription>Reflects the current state of the project across all views.</FieldDescription>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <div className="flex items-center justify-end pt-2">
                <PermissionTooltip allowed={!disabled} message="You don't have permission to update this project.">
                  <Button size="sm" disabled={mutation.isPending || disabled}>
                    <PencilIcon />
                    {mutation.isPending ? "Saving..." : "Save changes"}
                  </Button>
                </PermissionTooltip>
              </div>
            </FieldGroup>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
